import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Typography, CircularProgress } from '@mui/material'

import { ReCaptcha, ThemeButton } from '../../../../ui'
import { useInspectionsContext } from '../../contexts'
import { useAjaxContext } from '../../../../core/ajax'
import { feedback } from '../../../../core/feedback'
import { getEnv } from '../../../../config/env'

/**
 * Componente de paso de verificación de reCAPTCHA para el flujo de inspecciones IP.
 *
 * Este es el primer paso del formulario de registro de IP, que requiere que el usuario
 * complete un reCAPTCHA de máxima seguridad antes de poder acceder al formulario.
 *
 * El componente valida el captcha con el backend mediante el endpoint /api/captcha/verify
 * antes de permitir al usuario avanzar al siguiente paso.
 *
 * @component
 */
export const CaptchaStep = () => {
  const { formatMessage } = useIntl()
  const { nextStep } = useInspectionsContext()
  const { post } = useAjaxContext()
  const recaptchaRef = useRef()

  const [token, setToken] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [validating, setValidating] = useState(false)
  const [validated, setValidated] = useState(false)

  const handleCaptchaChange = async (value) => {
    setToken(value)
    setAttempted(false)
    setValidating(true)

    try {
      const response = await post('captcha/verify', { token: value })

      if (response.data.success && response.data.validated) {
        setValidated(true)
        feedback('success', formatMessage({ id: 'pages.inspections.steps.captcha.validated' }))
      } else {
        setValidated(false)
        setToken('')
        if (recaptchaRef.current) {
          recaptchaRef.current.reset()
        }
        feedback('error', formatMessage({ id: 'pages.inspections.steps.captcha.validationFailed' }))
      }
    } catch (error) {
      setValidated(false)
      setToken('')
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
      const errorMessage =
        error?.response?.data?.message ||
        formatMessage({ id: 'pages.inspections.steps.captcha.validationError' })
      feedback('error', errorMessage)
    } finally {
      setValidating(false)
    }
  }

  const handleCaptchaExpired = () => {
    setToken('')
    setAttempted(false)
    setValidated(false)
    feedback('warning', formatMessage({ id: 'pages.inspections.steps.captcha.expired' }))
  }

  const handleNext = (e) => {
    e.preventDefault()
    setAttempted(true)

    if (!validated) {
      return
    }

    nextStep()
  }

  const handleReset = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset()
    }
    setToken('')
    setAttempted(false)
    setValidated(false)
  }

  const siteKey = getEnv('REACT_APP_RECAPTCHA_SITE_KEY')

  return (
    <Box>
      <Box textAlign="center" mb={4}>
        <Typography variant="h5" color="primary" gutterBottom>
          {formatMessage({ id: 'pages.inspections.steps.captcha.title' })}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {formatMessage({ id: 'pages.inspections.steps.captcha.subtitle' })}
        </Typography>
      </Box>

      <form onSubmit={handleNext}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
          {validating && (
            <Box display="flex" alignItems="center" mb={2}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2" color="textSecondary">
                {formatMessage({ id: 'pages.inspections.steps.captcha.validating' })}
              </Typography>
            </Box>
          )}

          <ReCaptcha
            ref={recaptchaRef}
            siteKey={siteKey}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            error={attempted && !validated}
            errorMessage={formatMessage({ id: 'pages.inspections.steps.captcha.error' })}
            theme="light"
            hl="es"
          />
        </Box>

        <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
          <ThemeButton
            variant="outlined"
            color="default"
            onClick={handleReset}
            disabled={!token && !validated}
          >
            {formatMessage({ id: 'pages.inspections.steps.captcha.resetBtn' })}
          </ThemeButton>

          <ThemeButton type="submit" color="secondary" disabled={!validated}>
            {formatMessage({ id: 'pages.inspections.steps.captcha.continueBtn' })}
          </ThemeButton>
        </Box>
      </form>
    </Box>
  )
}
